import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import * as Sentry from "@sentry/react";

import {
  APP_VERSION_CACHE_KEY,
  APP_VERSION_RECHECK_AFTER_MS,
} from "../constants/appVersion";
import { ENABLE_APP_VERSION_LOGS } from "../constants/logging";
import { fetchAppVersionConfig, type AppVersionConfig, type PlatformVersionInfo } from "../connectors/appVersionApi";
import { computeVersionStatus, type AppVersionStatus } from "../utils/versionGate";

const log = (...args: unknown[]) => {
  if (!ENABLE_APP_VERSION_LOGS) return;
  // eslint-disable-next-line no-console
  console.log("[AppVersionGate]", ...args);
};

const warn = (...args: unknown[]) => {
  if (!ENABLE_APP_VERSION_LOGS) return;
  // eslint-disable-next-line no-console
  console.warn("[AppVersionGate]", ...args);
};

interface CachedConfig {
  config: AppVersionConfig;
  timestamp: number;
}

const readCache = async (): Promise<CachedConfig | null> => {
  try {
    const { value } = await Preferences.get({ key: APP_VERSION_CACHE_KEY });
    if (!value) return null;
    const parsed = JSON.parse(value);
    if (!parsed?.config?.ios || !parsed?.config?.android) return null;
    return parsed as CachedConfig;
  } catch (error) {
    warn("cache read failed", error);
    return null;
  }
};

const writeCache = async (config: AppVersionConfig): Promise<void> => {
  try {
    const payload: CachedConfig = { config, timestamp: Date.now() };
    await Preferences.set({
      key: APP_VERSION_CACHE_KEY,
      value: JSON.stringify(payload),
    });
  } catch (error) {
    warn("cache write failed", error);
  }
};

const pickPlatform = (config: AppVersionConfig) => {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return config.ios;
  if (platform === "android") return config.android;
  return null;
};

const reportStatusToSentry = (status: AppVersionStatus, nativeVersion: string | null) => {
  if (status !== "forced" && status !== "suggested") return;
  try {
    Sentry.captureMessage(`[AppVersionGate] ${status}`, {
      level: "info",
      tags: { app_version_gate: status },
      extra: { nativeVersion },
    });
  } catch {
    // Sentry must never block UX.
  }
};

export interface AppVersionGateResult {
  status: "loading" | AppVersionStatus;
  latest: string | null;
  platformInfo: PlatformVersionInfo | null;
  refetch: () => void;
}

export const useAppVersionGate = (): AppVersionGateResult => {
  const isNative = Capacitor.isNativePlatform();
  const [status, setStatus] = useState<"loading" | AppVersionStatus>(
    isNative ? "loading" : "ok",
  );
  const [latest, setLatest] = useState<string | null>(null);
  const [platformInfo, setPlatformInfo] = useState<PlatformVersionInfo | null>(null);
  const backgroundedAtRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const evaluate = useCallback(async (): Promise<void> => {
    if (!isNative) return;
    if (inFlightRef.current) {
      log("evaluate: skip, in-flight");
      return;
    }
    inFlightRef.current = true;

    try {
      const builtin = await CapacitorUpdater.getBuiltinVersion().catch(() => null);
      const nativeVersion = builtin?.version ?? null;
      log("evaluate: native version", nativeVersion);

      let config: AppVersionConfig | null = null;
      try {
        config = await fetchAppVersionConfig();
        log("evaluate: fetched", config);
        await writeCache(config);
      } catch (error) {
        warn("evaluate: fetch failed, trying cache", error);
        const cached = await readCache();
        config = cached?.config ?? null;
      }

      if (!config) {
        log("evaluate: no config (no cache, fetch failed) → ok");
        setStatus("ok");
        setLatest(null);
        setPlatformInfo(null);
        return;
      }

      const resolvedPlatformInfo = pickPlatform(config);
      if (!resolvedPlatformInfo) {
        log("evaluate: unrecognized platform → ok");
        setStatus("ok");
        setLatest(null);
        setPlatformInfo(null);
        return;
      }

      const nextStatus = computeVersionStatus(
        nativeVersion,
        resolvedPlatformInfo.latest,
        resolvedPlatformInfo.minimum,
      );
      log("evaluate: result", { nextStatus, ...resolvedPlatformInfo });
      setStatus(nextStatus);
      setLatest(resolvedPlatformInfo.latest);
      setPlatformInfo(resolvedPlatformInfo);
      reportStatusToSentry(nextStatus, nativeVersion);
    } finally {
      inFlightRef.current = false;
    }
  }, [isNative]);

  // Initial evaluation on mount.
  useEffect(() => {
    if (!isNative) return;
    void evaluate();
  }, [evaluate, isNative]);

  // Re-evaluate on resume only if the app was truly in background long enough.
  // A pause listener records when we went to background; the resume listener
  // compares that timestamp against APP_VERSION_RECHECK_AFTER_MS so that quick
  // foreground/background cycles (notification checks, app switches) and any
  // resume mid-operation never trigger a forced-update screen.
  useEffect(() => {
    if (!isNative) return;
    let pauseListener: { remove: () => void } | undefined;
    let resumeListener: { remove: () => void } | undefined;
    let cancelled = false;

    void (async () => {
      const pauseHandle = await CapApp.addListener("pause", () => {
        backgroundedAtRef.current = Date.now();
        log("pause: backgrounded at", backgroundedAtRef.current);
      });
      if (cancelled) {
        pauseHandle?.remove?.();
        return;
      }
      pauseListener = pauseHandle;

      const resumeHandle = await CapApp.addListener("resume", () => {
        const backgroundedAt = backgroundedAtRef.current;
        backgroundedAtRef.current = null;
        if (backgroundedAt === null) {
          log("resume: skip, no pause timestamp recorded");
          return;
        }
        const duration = Date.now() - backgroundedAt;
        if (duration < APP_VERSION_RECHECK_AFTER_MS) {
          log("resume: skip, background duration too short", { duration });
          return;
        }
        log("resume: re-evaluating after background duration", { duration });
        void evaluate();
      });
      if (cancelled) {
        resumeHandle?.remove?.();
        return;
      }
      resumeListener = resumeHandle;
    })();

    return () => {
      cancelled = true;
      pauseListener?.remove?.();
      resumeListener?.remove?.();
    };
  }, [evaluate, isNative]);

  const refetch = useCallback(() => void evaluate(), [evaluate]);
  return { status, latest, platformInfo, refetch };
};
